import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface InsightCardProps {
  title: string;
  body: string;
  linkLabel: string;
  to: string;
  className?: string;
}

// Decorative block mosaic (top-right of the reference tips card).
const MOSAIC = [
  [0, 0, 0, 1],
  [0, 0, 2, 1],
  [0, 3, 3, 1],
  [3, 2, 3, 1],
];
const MOSAIC_FILL = ["", "bg-stripes-brand", "bg-chart-5/70", "bg-brand-soft"];

export function InsightCard({ title, body, linkLabel, to, className }: InsightCardProps) {
  return (
    <section className={cn("relative overflow-hidden rounded-card bg-card p-6 flex flex-col", className)}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-medium leading-tight tracking-tight">{title}</h2>
          <p className="text-[13px] text-foreground/80 mt-2">{body}</p>
        </div>
        {/* In-flow (not absolute) so text never runs underneath it */}
        <div aria-hidden className="hidden sm:grid shrink-0 grid-cols-4 gap-[3px] opacity-90">
          {MOSAIC.flat().map((v, i) => (
            <span key={i} className={cn("h-5 w-5 xl:h-6 xl:w-6 rounded-[5px]", MOSAIC_FILL[v])} />
          ))}
        </div>
      </div>
      <Link to={to} className="relative mt-auto pt-5 inline-flex items-center gap-1 text-sm font-medium hover:text-brand-strong w-fit">
        {linkLabel} <ChevronRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

interface PromoCardProps {
  title: string;
  body: string;
  cta: string;
  to: string;
  icon: React.ReactNode;
  onDismiss?: () => void;
}

/** Sidebar prompt card (e.g. low credit balance). */
export function PromoCard({ title, body, cta, to, icon, onDismiss }: PromoCardProps) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start justify-between">
        <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center">{icon}</span>
        {onDismiss && (
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            className="h-6 w-6 rounded-md border border-border text-muted-foreground hover:text-foreground flex items-center justify-center text-xs"
          >
            ✕
          </button>
        )}
      </div>
      <p className="text-lg font-medium mt-4 leading-tight">{title}</p>
      <p className="text-[13px] text-foreground/75 mt-1.5">{body}</p>
      <Link
        to={to}
        className="mt-4 h-10 w-full rounded-xl bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center hover:bg-primary/85 transition-colors"
      >
        {cta}
      </Link>
    </div>
  );
}
